import { Workspace } from '@sr-sdks/api-sdk-axios';
import {
  canAccessRoyalties as checkCanAccessRoyalties,
  canAccessRoyaltiesAnz as checkCanAccessRoyaltiesAnz,
  canAccessRoyaltiesWorld as checkCanAccessRoyaltiesWorld,
} from '../utils/helpers/common';
import { useEffectiveMembershipStatus } from './useEffectiveMembershipStatus';
import { useWorkspaceMembership } from './useWorkspaceMembership';

interface UseCanAccessRoyaltiesResult {
  /**
   * True when the workspace is a Current Member and has any royalties addon.
   * Gates Historic Royalties (MSR2) nav + routes only — the region pages use
   * the two flags below. False while access is still resolving, or while
   * `enabled` is false, so nav does not flash the wrong state.
   */
  canAccessRoyalties: boolean;
  /**
   * True when the workspace is a Current Member and has
   * `anzSecondaryRoyalties`. Gates Royalties ANZ nav. False while access is
   * still resolving, or while `enabled` is false.
   */
  canAccessRoyaltiesAnz: boolean;
  /**
   * True when the workspace is a Current Member and has `royaltiesWorld`.
   * Gates Royalties World nav. False while access is still resolving,
   * or while `enabled` is false.
   */
  canAccessRoyaltiesWorld: boolean;
  /** Resolved Current Member flag (membership record preferred when available). */
  isCurrentMember: boolean;
  /**
   * True while membership (or a workspace refresh for users without
   * ViewMemberships) is still in flight.
   */
  isLoadingMembership: boolean;
}

/**
 * Shared royalties-access check for nav links.
 *
 * Historic uses {@link canAccessRoyalties} (any royalties addon); ANZ uses
 * {@link canAccessRoyaltiesAnz} and World
 * {@link canAccessRoyaltiesWorld}, so a workspace holding one region's addon
 * never gets a link to the other region's permanently empty table. All three
 * share one membership fetch.
 *
 * @param enabled Extra gate (e.g. payments feature flag) applied on top of
 *   the membership permission check — skip the fetch while the feature is off.
 *   Access flags are false while this is false, even if the cached workspace
 *   would otherwise qualify.
 */
export const useCanAccessRoyalties = (
  workspace?: Workspace,
  enabled = true,
) => {
  const {
    hasBlockingWorkspaceRefreshError,
    isResolving,
    query: membershipQuery,
  } = useWorkspaceMembership(workspace, enabled);
  const { isCurrentMember } = useEffectiveMembershipStatus(
    workspace,
    membershipQuery.data,
  );

  const accessSettled =
    enabled && !isResolving && !hasBlockingWorkspaceRefreshError;

  return {
    canAccessRoyalties:
      accessSettled && checkCanAccessRoyalties(isCurrentMember, workspace),
    canAccessRoyaltiesAnz:
      accessSettled && checkCanAccessRoyaltiesAnz(isCurrentMember, workspace),
    canAccessRoyaltiesWorld:
      accessSettled && checkCanAccessRoyaltiesWorld(isCurrentMember, workspace),
    isCurrentMember,
    isLoadingMembership: isResolving,
  } satisfies UseCanAccessRoyaltiesResult;
};
